import { motion } from "framer-motion";
import { TEST_IDS } from "@/constants/testIds";

const STEPS = [
  {
    n: "01",
    title: "Send the sermon.",
    desc:
      "Drop a YouTube link, a Vimeo file, or a Google Drive folder. That's it — no software, no long onboarding form.",
    img: "https://images.pexels.com/photos/9866942/pexels-photo-9866942.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    caption: "Send",
  },
  {
    n: "02",
    title: "We clip it.",
    desc:
      "Our editors pull the moments that stop the scroll — a story, a challenge, a benediction. Captions, titles, and your church's look, baked in.",
    img: "https://images.unsplash.com/photo-1781966995939-3748dc1f1742?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHx2aWRlbyUyMGVkaXRvciUyMGRlc2slMjBkYXJrfGVufDB8fHx8MTc4NDU3ODcyNnww&ixlib=rb-4.1.0&q=85",
    caption: "Clip",
  },
  {
    n: "03",
    title: "You post it.",
    desc:
      "Get finished 9:16 clips (and 1:1 if you want them) delivered to Drive, ready for Reels, Shorts, and TikTok. Suggested captions included.",
    img: "https://images.pexels.com/photos/8815046/pexels-photo-8815046.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    caption: "Post",
  },
];

const rowVariants = {
  hidden: { opacity: 0, y: 60 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.2, 0.8, 0.2, 1] } },
};

export default function Process() {
  return (
    <section
      id="process"
      data-testid={TEST_IDS.process.root}
      className="py-24 md:py-40 border-t border-[#E5E1DA]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
          className="mb-20 md:mb-28 grid md:grid-cols-12 gap-6 items-end"
        >
          <div className="md:col-span-2 eyebrow">The manifesto</div>
          <h2 className="md:col-span-8 font-serif-display font-black tracking-tight text-[clamp(38px,6vw,84px)] leading-[0.95]">
            Three chapters.
            <br />
            <span className="italic text-[#E07A5F]">One quiet promise.</span>
          </h2>
          <div className="md:col-span-2 text-right hidden md:block">
            <span className="font-mono-ui text-[11px] tracking-[0.24em] text-[#787672] uppercase">§ 01 — 03</span>
          </div>
        </motion.div>

        <div className="space-y-24 md:space-y-40">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.n}
              data-testid={TEST_IDS.process.step(i + 1)}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
              variants={rowVariants}
              className={`grid md:grid-cols-12 gap-8 md:gap-14 items-center ${
                i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""
              }`}
            >
              <div className="md:col-span-6 relative">
                <div className="absolute -top-16 md:-top-20 -left-2 select-none pointer-events-none">
                  <span className={i === 1 ? "chapter-number-solid" : "chapter-number"}>
                    {s.n}
                  </span>
                </div>
                <div className="relative aspect-[4/5] md:aspect-[4/5] overflow-hidden rounded-[28px] border border-black/5">
                  <img
                    src={s.img}
                    alt={s.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <div className="absolute bottom-4 left-4 text-white/90 font-mono-ui text-[11px] tracking-[0.24em] uppercase">
                    {s.caption} · Fig.{i + 1}
                  </div>
                </div>
              </div>

              <div className="md:col-span-6 md:pl-6">
                <div className="eyebrow mb-4">Chapter {s.n}</div>
                <h3 className="font-serif-display text-[clamp(30px,4.5vw,56px)] leading-[1.02] tracking-tight max-w-[18ch]">
                  {s.title}
                </h3>
                <p className="mt-6 text-[17px] md:text-[19px] text-[#1C1C1A]/75 leading-relaxed max-w-[46ch]">
                  {s.desc}
                </p>
                <div className="mt-8 h-px bg-[#E5E1DA] w-full max-w-[240px]" />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
